import type React from "react";
import {useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {
    Alert,
    Button,
    Divider,
    Drawer,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {AdminPanelSettings, Close} from "@mui/icons-material";
import {useStores} from "~/stores";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {deepestLevel} from "~/pages/taxonomy-builder-v2/services/taxonomyCompleteness";
import {useTaxonomyListQuery} from "~/pages/taxonomy-builder-v2/hooks/useTaxonomyListQuery";
import {
    type TaxonomySettings,
    useSaveTaxonomySettingsMutation,
} from "~/pages/taxonomy-builder-v2/hooks/useSaveTaxonomySettingsMutation";

/** Depths offered, as in the current builder's Size field. */
const DEPTHS = [1, 2, 3, 4, 5, 6, 7, 8];
const DRAWER_WIDTH = 400;

type TaxonomySettingsDrawerProps = {
    taxonomy: m_taxonomy.FullSerializer;
    onClose: () => void;
};

/**
 * The Mithra staff settings of the current builder's toolbar, in a drawer: the taxonomy's name, its depth
 * (the levels every category needs before sending for approval) and the taxonomy the databag is linked to.
 * Mount it fresh for each opening: the fields start from the taxonomy as loaded.
 */
export const TaxonomySettingsDrawer: React.FC<TaxonomySettingsDrawerProps> = observer(({taxonomy, onClose}) => {
    const {bagStore, p} = useStores();
    const [settings, setSettings] = useState<TaxonomySettings>({
        name: taxonomy.name,
        size: taxonomy.size,
        linkedTaxonomyId: taxonomy.id,
    });
    const {data: taxonomies, isLoading: isLoadingList} = useTaxonomyListQuery(true);
    const {mutate, isPending} = useSaveTaxonomySettingsMutation();
    const deepest = useMemo(() => deepestLevel(taxonomy.diff_tree), [taxonomy.diff_tree]);

    const databagId = bagStore.bagId;
    const set = <K extends keyof TaxonomySettings>(key: K, value: TaxonomySettings[K]) =>
        setSettings(prev => ({...prev, [key]: value}));
    const name = settings.name.trim();
    const changed = name !== taxonomy.name || settings.size !== taxonomy.size || settings.linkedTaxonomyId !== taxonomy.id;
    const save = () => mutate({taxonomy, databagId, settings: {...settings, name}}, {onSuccess: onClose});

    return (
        <Drawer
            anchor="right"
            open
            onClose={isPending ? undefined : onClose}
            slotProps={{paper: {sx: {width: DRAWER_WIDTH, maxWidth: '100vw'}}}}
        >
            <Stack sx={{height: '100%'}}>
                <Stack direction="row" alignItems="center" gap={1} sx={{px: 3, py: 2}}>
                    <AdminPanelSettings color="action"/>
                    <Typography variant="h6" fontWeight={700} sx={{flex: 1}}>Taxonomy settings</Typography>
                    <IconButton aria-label="Close settings" onClick={onClose} disabled={isPending} size="small">
                        <Close/>
                    </IconButton>
                </Stack>
                <Divider/>
                <Stack gap={3} sx={{flex: 1, overflowY: 'auto', px: 3, py: 3}}>
                    <Typography variant="body2" color="text.secondary">
                        Only Mithra staff can see and change these settings.
                    </Typography>
                    <TextField
                        label="Taxonomy name"
                        value={settings.name}
                        onChange={e => set('name', e.target.value)}
                        error={!name}
                        helperText={!name ? 'The name cannot be empty.' : undefined}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        select
                        label="Depth"
                        value={settings.size}
                        onChange={e => set('size', Number(e.target.value))}
                        helperText={`Every category needs sub-categories down to this level before the taxonomy can be sent for approval. The categories now go down to L${deepest}.`}
                        size="small"
                        fullWidth
                    >
                        {DEPTHS.map(depth => <MenuItem key={depth} value={depth}>L{depth}</MenuItem>)}
                    </TextField>
                    <TextField
                        select
                        label="Linked taxonomy"
                        value={settings.linkedTaxonomyId}
                        onChange={e => set('linkedTaxonomyId', Number(e.target.value))}
                        disabled={isLoadingList || !!p.p.hardcodeTaxonomyId}
                        helperText={p.p.hardcodeTaxonomyId
                            ? 'This environment always shows the same taxonomy, so the link cannot be changed here.'
                            : 'The taxonomy this databag uses.'}
                        size="small"
                        fullWidth
                    >
                        {/* The current one until the list has loaded, so the field shows it straight away. */}
                        {(taxonomies ?? [taxonomy]).map(t => <MenuItem key={t.id} value={t.id}>[{t.id}] {t.name}</MenuItem>)}
                    </TextField>
                    {settings.linkedTaxonomyId !== taxonomy.id && (
                        <Alert severity="warning">
                            Saving links databag {databagId} to another taxonomy. The builder then shows that taxonomy,
                            and this one's draft stays as it is.
                        </Alert>
                    )}
                </Stack>
                <Divider/>
                <Stack direction="row" justifyContent="flex-end" gap={1} sx={{px: 3, py: 2}}>
                    <Button onClick={onClose} disabled={isPending}>Cancel</Button>
                    <Button variant="contained" onClick={save} loading={isPending} disabled={!changed || !name}>
                        Save
                    </Button>
                </Stack>
            </Stack>
        </Drawer>
    );
});
